/* eslint-disable @next/next/no-before-interactive-script-outside-document */
/* eslint-disable @next/next/no-document-import-in-page */
/* eslint-disable @next/next/no-title-in-document-head */
import { useMemo } from 'react';

import Script from 'next/script';
import { useRouter } from 'next/router';
import Head from 'next/head';
import { AppProps } from 'next/dist/shared/lib/router/router';

import { SEOLayout } from '~/layouts/seo';
import { PublicLayout } from '~/layouts/public';
import { PrivateLayout } from '~/layouts/private';

import { ProvidersHOC } from '~/common/providers';
import { sidebar } from '~/common/constants/sidebar';

function ValidateUrl() {
  if (typeof window !== 'undefined') {
    let urlUpdated = false;
    let url = window.location.href;

    // Redirect to HTTPS
    if (url.substr(0, 5) !== 'https' && !url.includes('localhost')) {
      url = url.replace('http', 'https');
      urlUpdated = true;
    }

    // Redirect from www to non-www
    if (url.includes('www.')) {
      url = url.replace('www.', '');
      urlUpdated = true;
    }

    if (urlUpdated) {
      window.location.href = url;
    }
  }
}

function CCV2App({ Component, pageProps }: AppProps): JSX.Element {
  ValidateUrl();

  const router = useRouter();

  const Layout = useMemo(() => {
    if (['/sobre', '/contato', '/privacidade', '/privacy'].includes(router.asPath)) {
      return SEOLayout;
    }

    return router.asPath.includes('/favorites') ||
      sidebar.some((item) => router.asPath.replace('/', '').includes(item.key))
      ? PrivateLayout
      : PublicLayout;
  }, [router]);

  return (
    <>
      <Head>
        <link rel="manifest" href="/manifest.json" />

        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
        <meta name="theme-color" content="#ffffff" />

        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon-32x32.png"
        />

        <meta charSet="utf-8" />

        <title>CryptoControl</title>

        <meta name="robots" content="index, follow" />

        <meta name="description" content="CryptoControl é um agregador de notícias de criptomoedas que oferece uma visão abrangente do mercado, permitindo que os usuários acompanhem as últimas notícias, tendências e análises em tempo real." />

        <meta
          name="facebook-domain-verification"
          content="m3vec4sb20g1zutxb39pw5mle3bdp6"
        />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="CryptoControl" />
        <meta property="og:title" content="CryptoControl" />
        <meta property="og:description" content="CryptoControl é um agregador de notícias de criptomoedas que oferece uma visão abrangente do mercado, permitindo que os usuários acompanhem as últimas notícias, tendências e análises em tempo real." />
        <meta property="og:image" content="https://cryptocontrol.pro/og/cryptocontrol_1200x630.png" />
        <meta property="og:image:alt" content="CryptoControl" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content="https://cryptocontrol.pro/" />
        <meta property="og:locale" content="pt_BR" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="CryptoControl" />
        <meta name="twitter:site" content="@CryptoControlX" />
        <meta name="twitter:description" content="CryptoControl é um agregador de notícias de criptomoedas que oferece uma visão abrangente do mercado, permitindo que os usuários acompanhem as últimas notícias, tendências e análises em tempo real." />
        <meta name="twitter:image" content="https://cryptocontrol.pro/og/cryptocontrol_1200x630.png" />

        <meta name="apple-itunes-app" content="app-id=6479964289" />

        {/* SCHEMA.ORG - DADOS ESTRUTURADOS */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@graph": [
                {
                  "@type": "Organization",
                  "@id": "https://cryptocontrol.pro/#organization",
                  "name": "CryptoControl",
                  "alternateName": "CryptoControl Tecnologia LTDA",
                  "url": "https://cryptocontrol.pro",
                  "logo": {
                    "@type": "ImageObject",
                    "url": "https://cryptocontrol.pro/og/cryptocontrol_1200x630.png",
                    "width": 1200,
                    "height": 630
                  },
                  "image": "https://cryptocontrol.pro/og/cryptocontrol_1200x630.png",
                  "description": "Plataforma de gestão e controle de criptomoedas. Acompanhe seu portfólio, transações e performance em tempo real.",
                  "foundingDate": "2023",
                  "legalName": "CryptoControl Tecnologia LTDA",
                  "taxID": "52.905.625/0001-04",
                  "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "Av. Paulista, 1000, Sala 101",
                    "addressLocality": "São Paulo",
                    "addressRegion": "SP",
                    "postalCode": "01310-100",
                    "addressCountry": "BR"
                  },
                  "contactPoint": [
                    {
                      "@type": "ContactPoint",
                      "telephone": "+55-11-96742-8387",
                      "contactType": "customer service",
                      "email": "contact@cryptocontrol.pro",
                      "availableLanguage": ["Portuguese", "English"],
                      "areaServed": "BR"
                    },
                    {
                      "@type": "ContactPoint",
                      "telephone": "+55-11-96742-8387",
                      "contactType": "sales",
                      "email": "contact@cryptocontrol.pro",
                      "availableLanguage": "Portuguese"
                    }
                  ],
                  "sameAs": [
                    "https://www.facebook.com/profile.php?id=61559590404672",
                    "https://www.instagram.com/cryptocontrol.pro",
                    "https://x.com/CryptoControlX",
                  ]
                },
                {
                  "@type": "WebSite",
                  "@id": "https://cryptocontrol.pro/#website",
                  "url": "https://cryptocontrol.pro",
                  "name": "CryptoControl",
                  "description": "Plataforma de gestão de criptomoedas",
                  "publisher": {
                    "@id": "https://cryptocontrol.pro/#organization"
                  },
                  "inLanguage": "pt-BR"
                },
                {
                  "@type": "WebPage",
                  "@id": "https://cryptocontrol.pro/#webpage",
                  "url": "https://cryptocontrol.pro",
                  "name": "CryptoControl - Gestão Inteligente de Criptomoedas",
                  "isPartOf": {
                    "@id": "https://cryptocontrol.pro/#website"
                  },
                  "about": {
                    "@id": "https://cryptocontrol.pro/#organization"
                  },
                  "description": "Plataforma líder em gestão de criptomoedas no Brasil. Acompanhe seu portfólio, transações e performance em tempo real.",
                  "inLanguage": "pt-BR"
                },
                {
                  "@type": "SoftwareApplication",
                  "name": "CryptoControl",
                  "operatingSystem": "iOS, Android, Web",
                  "applicationCategory": "FinanceApplication",
                  "offers": {
                    "@type": "Offer",
                    "price": "0",
                    "priceCurrency": "BRL"
                  },
                  "aggregateRating": {
                    "@type": "AggregateRating",
                    "ratingValue": "4.8",
                    "ratingCount": "1250"
                  }
                }
              ]
            })
          }}

        />
      </Head>

      <Script strategy="beforeInteractive" src="/js/googletagmanager.js" />
      <Script strategy="beforeInteractive" src="/js/cookie-management.js" />
      <Script async src="https://js.stripe.com/v3/pricing-table.js" />

      <ProvidersHOC>
        <Layout>
          <Component {...pageProps} />
        </Layout>
      </ProvidersHOC>

      {/* NOSCRIPT - CONTEÚDO PARA CRAWLERS SEM JS */}
      <noscript>
        <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto', fontFamily: 'Roboto, sans-serif' }}>
          <h1>CryptoControl - Gestão Inteligente de Criptomoedas</h1>
          <p>
            A CryptoControl é a plataforma líder em gestão de criptomoedas no Brasil.
            Acompanhe seu portfólio, transações e performance em tempo real.
          </p>
          <h2>Sobre a Empresa</h2>
          <p>
            Fundada em 2023, a CryptoControl Tecnologia LTDA oferece soluções
            profissionais para gestão de criptoativos.
          </p>
          <h2>Contato</h2>
          <address>
            <p>CryptoControl Tecnologia LTDA</p>
            <p>CNPJ: 52.905.625/0001-04</p>
            <p>Av. Paulista, 1000, Sala 101</p>
            <p>São Paulo - SP, CEP 01310-100</p>
            <p>Email: contact@cryptocontrol.pro</p>
            <p>Telefone: +55 (11) 96742-8387</p>
          </address>
          <nav>
            <a href="/sobre">Sobre Nós</a> |
            <a href="/contato">Contato</a> |
            <a href="/privacidade">Política de Privacidade</a> |
            <a href="/privacy">Privacy Policy</a>
          </nav>
        </div>
      </noscript>
    </>
  );
}

export default CCV2App;
